import React, { useState } from 'react';
import axios from 'axios';
import { API_URL, getAuthHeader } from '../../utils/config';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay,
         isSameMonth, addMonths, subMonths, isToday, parseISO } from 'date-fns';

function FullCalendarOverlay({ meetings, onClose, onSchedule, onRespond }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(new Date());

  const days = eachDayOfInterval({ start: startOfMonth(currentMonth), end: endOfMonth(currentMonth) });
  const startPad = startOfMonth(currentMonth).getDay();
  const getMeetingsForDay = (day) => meetings.filter(m => isSameDay(parseISO(m.scheduled_at), day));
  const selectedMeetings = getMeetingsForDay(selectedDay);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden"
        style={{maxHeight:'90vh'}}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700"
          style={{background:'linear-gradient(135deg,#0d9488,#2563eb)'}}>
          <div className="flex items-center gap-3">
            <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
              <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            <span className="text-white font-bold text-lg">Full Calendar</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onSchedule}
              className="px-4 py-1.5 bg-white/20 hover:bg-white/30 text-white text-sm font-semibold rounded-xl transition flex items-center gap-1.5">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Schedule
            </button>
            <button onClick={onClose} className="w-8 h-8 rounded-xl bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-4 h-4">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="flex" style={{height:'calc(90vh - 73px)'}}>
          {/* Left — calendar */}
          <div className="w-80 flex-shrink-0 border-r border-gray-100 dark:border-gray-700 p-4">
            {/* Month nav */}
            <div className="flex items-center justify-between mb-3">
              <button onClick={() => setCurrentMonth(subMonths(currentMonth,1))}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 transition">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
              <span className="text-sm font-bold text-gray-800 dark:text-gray-100">{format(currentMonth,'MMMM yyyy')}</span>
              <button onClick={() => setCurrentMonth(addMonths(currentMonth,1))}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 transition">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            </div>
            {/* Day headers */}
            <div className="grid grid-cols-7 mb-1">
              {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
                <div key={d} className="text-center text-xs font-medium text-gray-400 py-1">{d}</div>
              ))}
            </div>
            {/* Grid */}
            <div className="grid grid-cols-7 gap-y-1">
              {Array(startPad).fill(null).map((_,i) => <div key={`p${i}`}/>)}
              {days.map(day => {
                const hasMeetings = getMeetingsForDay(day).length > 0;
                const isSelected = isSameDay(day, selectedDay);
                const todayDay = isToday(day);
                return (
                  <button key={day.toString()} onClick={() => setSelectedDay(day)}
                    className={`relative flex flex-col items-center py-1.5 rounded-lg transition text-xs font-medium
                      ${isSelected ? 'bg-teal-500 text-white' : todayDay ? 'bg-teal-50 dark:bg-teal-900/20 text-teal-600' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}
                      ${!isSameMonth(day,currentMonth) ? 'opacity-30' : ''}`}>
                    {format(day,'d')}
                    {hasMeetings && <span className={`w-1.5 h-1.5 rounded-full mt-0.5 ${isSelected?'bg-white':'bg-teal-500'}`}/>}
                  </button>
                );
              })}
            </div>

            {/* Legend */}
            <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700 space-y-1">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Legend</p>
              <div className="flex items-center gap-2 text-xs text-gray-500"><span className="w-3 h-3 rounded-full bg-teal-500 inline-block"/>Meeting scheduled</div>
              <div className="flex items-center gap-2 text-xs text-gray-500"><span className="w-3 h-3 rounded-full bg-teal-200 inline-block"/>Today</div>
            </div>
          </div>

          {/* Right — day detail */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-bold text-gray-800 dark:text-gray-100">{isToday(selectedDay) ? 'Today' : format(selectedDay,'EEEE')}</p>
                <p className="text-sm text-gray-400">{format(selectedDay,'MMMM d, yyyy')}</p>
              </div>
              {selectedMeetings.length > 0 && (
                <span className="text-xs px-2.5 py-1 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 rounded-full font-semibold">
                  {selectedMeetings.length} meeting{selectedMeetings.length > 1 ? 's' : ''}
                </span>
              )}
            </div>

            {selectedMeetings.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-gray-400">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-12 h-12 mb-3 opacity-20">
                  <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/>
                </svg>
                <p className="text-sm font-medium">No meetings on this day</p>
                <button onClick={onSchedule} className="mt-3 text-sm text-teal-500 hover:text-teal-600 font-semibold transition">+ Schedule a meeting</button>
              </div>
            ) : (
              selectedMeetings.map(m => {
                const meetDate = parseISO(m.scheduled_at);
                return (
                  <div key={m.id} className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-4 mb-3 border border-gray-100 dark:border-gray-700">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <p className="font-bold text-gray-800 dark:text-gray-100 text-base">{m.title}</p>
                        {m.description && <p className="text-sm text-gray-400 mt-0.5">{m.description}</p>}
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${m.my_status==='accepted'?'bg-green-100 text-green-700':m.my_status==='declined'?'bg-red-100 text-red-600':'bg-amber-100 text-amber-700'}`}>
                        {m.my_status==='accepted'?'✓ Accepted':m.my_status==='declined'?'✗ Declined':'● Pending'}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-gray-500 mb-3">
                      <span className="flex items-center gap-1">🕐 {format(meetDate,'HH:mm')} IST</span>
                      <span className="flex items-center gap-1">⏱ {m.duration_minutes} min</span>
                      <span className="flex items-center gap-1">👤 {m.is_organizer ? 'You organized' : `By ${m.organizer_name}`}</span>
                    </div>
                    {m.meet_link && (
                      <a href={m.meet_link.startsWith('http') ? m.meet_link : `https://qtalk.qualtechedge.in${m.meet_link}`}
                        target="_blank" rel="noreferrer"
                        className="flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-teal-500 to-blue-500 hover:opacity-90 rounded-xl transition mb-2">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                          <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/>
                        </svg>
                        Join Qtalk Meeting
                      </a>
                    )}
                    {m.my_status === 'pending' && (
                      <div className="flex gap-2">
                        <button onClick={() => onRespond(m.id,'accepted')} className="flex-1 py-2 bg-green-500 text-white text-sm font-semibold rounded-xl hover:bg-green-600 transition">✓ Accept</button>
                        <button onClick={() => onRespond(m.id,'declined')} className="flex-1 py-2 bg-red-400 text-white text-sm font-semibold rounded-xl hover:bg-red-500 transition">✗ Decline</button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MeetingsCalendar({ meetings, onSchedule, onRespond }) {
  const [showFullCal, setShowFullCal] = useState(false);
  const [search, setSearch] = useState('');

  const now = new Date();
  const upcoming = meetings
    .filter(m => new Date(m.scheduled_at) >= now && m.my_status !== 'declined')
    .sort((a,b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));

  const past = meetings
    .filter(m => new Date(m.scheduled_at) < now && m.my_status !== 'declined')
    .sort((a,b) => new Date(b.scheduled_at) - new Date(a.scheduled_at));

  const filtered = (list) => list.filter(m =>
    !search || m.title.toLowerCase().includes(search.toLowerCase()) ||
    (m.organizer_name||'').toLowerCase().includes(search.toLowerCase())
  );

  const MeetingRow = ({ m }) => {
    const meetDate = parseISO(m.scheduled_at);
    const isPast = new Date(m.scheduled_at) < now;
    const todayMeet = isToday(meetDate);
    const [participants, setParticipants] = useState(null);
    const [showParticipants, setShowParticipants] = useState(false);
    const fetchParticipants = async () => {
      if (participants) { setShowParticipants(p=>!p); return; }
      try {
        const { data } = await axios.get(`${API_URL}/meetings/${m.id}/participants`, { headers: getAuthHeader() });
        setParticipants(data);
        setShowParticipants(true);
      } catch(e) {}
    };
    return (
      <div className={`rounded-xl border p-3 mb-2 transition ${isPast ? 'bg-gray-50/50 dark:bg-gray-800/50 border-gray-100 dark:border-gray-700 opacity-70' : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 shadow-sm'}`}>
        <div className="flex items-start gap-2.5">
          <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex flex-col items-center justify-center text-white font-bold ${todayMeet ? 'bg-teal-500' : isPast ? 'bg-gray-400' : 'bg-gradient-to-br from-blue-500 to-blue-600'}`}>
            <span style={{fontSize:'9px',lineHeight:1}}>{format(meetDate,'MMM').toUpperCase()}</span>
            <span style={{fontSize:'16px',lineHeight:1.1}}>{format(meetDate,'d')}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-1">
              <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{m.title}</p>
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${m.my_status==='accepted'?'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400':m.my_status==='declined'?'bg-red-100 text-red-600':'bg-amber-100 text-amber-700'}`}>
                {m.my_status==='accepted'?'✓':m.my_status==='declined'?'✗':'●'}
              </span>
            </div>
            <p className="text-xs text-teal-600 dark:text-teal-400 font-medium">{format(meetDate,'HH:mm')} · {m.duration_minutes} min</p>
            <p className="text-xs text-gray-400 truncate">{m.is_organizer ? 'You organized' : `By ${m.organizer_name}`}</p>
            {m.is_organizer && (
              <button onClick={fetchParticipants} className="text-xs text-teal-500 hover:text-teal-600 font-medium mt-0.5 transition">
                {showParticipants ? '▲ Hide' : '▼ Who is coming?'}
              </button>
            )}
          </div>
        </div>
        {/* RSVP status panel */}
        {showParticipants && participants && (
          <div className="mt-2 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
            {participants.map((p, i) => (
              <div key={p.id} className={`flex items-center gap-2.5 px-3 py-2 ${i < participants.length-1 ? 'border-b border-gray-100 dark:border-gray-700' : ''}`}>
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden">
                  {p.avatar_url?.startsWith('https://')
                    ? <img src={p.avatar_url} className="w-full h-full object-cover" alt=""/>
                    : p.full_name?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 truncate">
                    {p.full_name} {p.is_organizer && <span className="text-teal-500">(organizer)</span>}
                  </p>
                  <p className="text-xs text-gray-400">{p.designation||p.department||''}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold flex-shrink-0
                  ${p.status==='accepted' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : p.status==='declined' ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                  : 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'}`}>
                  {p.status==='accepted' ? '✓ Going' : p.status==='declined' ? '✗ Declined' : '? Pending'}
                </span>
              </div>
            ))}
          </div>
        )}
        {!isPast && m.meet_link && (
          <a href={m.meet_link.startsWith('http') ? m.meet_link : `https://qtalk.qualtechedge.in${m.meet_link}`}
            target="_blank" rel="noreferrer"
            className="mt-2 flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-teal-500 to-blue-500 hover:opacity-90 rounded-lg transition">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
              <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/>
            </svg>
            Join Meeting
          </a>
        )}
        {m.my_status === 'pending' && !isPast && (
          <div className="flex gap-2 mt-1.5">
            <button onClick={() => onRespond(m.id,'accepted')} className="flex-1 py-1.5 bg-green-500 text-white text-xs rounded-lg hover:bg-green-600 transition font-medium">✓ Accept</button>
            <button onClick={() => onRespond(m.id,'declined')} className="flex-1 py-1.5 bg-red-400 text-white text-xs rounded-lg hover:bg-red-500 transition font-medium">✗ Decline</button>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="flex flex-col h-full overflow-hidden">
        {/* Top actions */}
        <div className="px-3 pt-3 pb-2 flex-shrink-0 space-y-2">
          <button onClick={onSchedule}
            className="w-full py-2.5 bg-gradient-to-r from-teal-500 to-blue-500 text-white text-sm font-semibold rounded-xl hover:opacity-90 transition flex items-center justify-center gap-2">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Schedule Meeting
          </button>
          <button onClick={() => setShowFullCal(true)}
            className="w-full py-2 border-2 border-teal-200 dark:border-teal-700 text-teal-600 dark:text-teal-400 text-sm font-semibold rounded-xl hover:bg-teal-50 dark:hover:bg-teal-900/20 transition flex items-center justify-center gap-2">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
              <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            Full Calendar
          </button>
        </div>

        {/* Search */}
        <div className="px-3 pb-2 flex-shrink-0">
          <div className="relative">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input type="text" placeholder="Search meetings..." value={search} onChange={e=>setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-sm rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-400 transition"/>
          </div>
        </div>

        {/* Meeting list */}
        <div className="flex-1 overflow-y-auto px-3 pb-3">

          {/* Upcoming section — always show header */}
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-teal-500 inline-block"/>
            Upcoming
            {filtered(upcoming).length > 0 && <span className="text-teal-500 ml-0.5">· {filtered(upcoming).length}</span>}
          </p>
          {filtered(upcoming).length === 0 ? (
            <div className="flex flex-col items-center justify-center py-6 mb-3 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-dashed border-gray-200 dark:border-gray-700">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8 mb-2 text-gray-300">
                <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/>
              </svg>
              <p className="text-xs text-gray-400 font-medium">No upcoming meetings</p>
              <button onClick={onSchedule} className="mt-1.5 text-xs text-teal-500 hover:text-teal-600 font-semibold transition">+ Schedule one</button>
            </div>
          ) : (
            filtered(upcoming).map(m => <MeetingRow key={m.id} m={m}/>)
          )}

          {/* Past section */}
          {filtered(past).length > 0 && (
            <>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2 mt-2 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400 inline-block"/>
                Past · {filtered(past).length}
              </p>
              {filtered(past).map(m => <MeetingRow key={m.id} m={m}/>)}
            </>
          )}

          {/* Truly empty — no meetings at all */}
          {meetings.length === 0 && (
            <div className="flex flex-col items-center justify-center py-10 text-gray-400">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-12 h-12 mb-3 opacity-20">
                <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/>
              </svg>
              <p className="text-sm font-medium">No meetings yet</p>
              <button onClick={onSchedule} className="mt-2 text-sm text-teal-500 hover:text-teal-600 font-semibold transition">+ Schedule your first</button>
            </div>
          )}
        </div>
      </div>

      {showFullCal && (
        <FullCalendarOverlay
          meetings={meetings}
          onClose={() => setShowFullCal(false)}
          onSchedule={() => { setShowFullCal(false); onSchedule(); }}
          onRespond={onRespond}
        />
      )}
    </>
  );
}
